// Liquidacion con montos del proveedor (LQ-4, ADR-026 §6). Con el filtro en
// "Todas", una fila por planta habilitada con el total del periodo (lectura
// multiplanta de ADR-025: el conjunto de plantas lo resuelve la API en cada
// request); con una planta, el borrador del periodo: total, viajes cobrados y
// sin tarifa, totales por concepto, los viajes con sus lineas explicadas y
// la descarga con montos. Una planta en modalidad exportable sin montos lo
// dice en castellano y manda al Exportable: UCORP no tiene montos de ese par.
//
// LQ-6 (ADR-026 §5): el estado del periodo (abierto, en revision, cerrado), el
// plazo para observar, la conformidad de la empresa sobre el borrador vigente
// (su hash), las observaciones de las dos partes con su resolucion y las
// notas de ajuste imputadas al periodo. UCORP cierra desde la consola; lo
// cerrado se lee de lo congelado y no cambia.
//
// La fecha por defecto es hoy en la zona de cada planta, leida de la API
// (regla 9). Listados con tope y "Ver mas" (rediseño, decision 1). Montos
// formateados desde enteros (logic/settlement.ts), nunca double.
import { Fragment, useEffect, useId, useRef, useState } from "react";
import type { ProviderSettlementSummary, SettlementDisputeReason, SettlementDraft, SettlementReview, TripSettlement, TripWait } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, PanelHeader } from "../components/ui/card.js";
import { EmptyState } from "../components/ui/empty.js";
import { Failure } from "../components/ui/failure.js";
import { Icon } from "../components/ui/icon.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { SkeletonTable } from "../components/ui/skeleton.js";
import { formatCalendarDate } from "../logic/documents.js";
import { hrefFor } from "../logic/navigation.js";
import {
  ADJUSTMENT_REASON_LABEL,
  APPROVAL_TEXT,
  CONCEPT_LABEL,
  DISPUTE_COMMENT_MAX,
  DISPUTE_COMMENT_PROBLEM_TEXT,
  DISPUTE_REASONS,
  DISPUTE_REASON_LABEL,
  DISPUTE_RESOLUTION_LABEL,
  EXPORT_ONLY_COPY,
  PARTY_LABEL,
  PERIOD_STATUS_LABEL,
  SETTLEMENT_SITES_SHOWN,
  SETTLEMENT_TRIPS_SHOWN,
  WAIT_SOURCE_LABEL,
  approvalState,
  disputeCommentProblem,
  disputeRequest,
  formatMinor,
  isCalendarDate,
  limited,
  lineText,
  summaryForSite,
  summaryTotalText,
  tripTotalText,
  tripsCountText,
  waitBillableText,
  waitBillingText,
  waitFallbackText,
  waitMinutesText,
  waitStopRows,
  waitsByTrip,
} from "../logic/settlement.js";
import { OUTCOME_LABEL, serviceLabel } from "../logic/trip-export.js";
import type { SectionContext } from "../shell/types.js";
import type { SaveFile } from "./TripExport.js";
import { saveWithAnchor } from "./TripExport.js";
import { SettlementError, type SettlementPort } from "./settlement-port.js";

interface Props {
  context: SectionContext;
  port: SettlementPort;
  save?: SaveFile;
}

type SummaryState =
  | { estado: "cargando" }
  | { estado: "listo"; items: readonly ProviderSettlementSummary[] }
  | { estado: "error"; kind: SettlementError["kind"] };

type DraftState =
  | { estado: "inicial" }
  | { estado: "cargando" }
  | { estado: "listo"; date: string; draft: SettlementDraft }
  | { estado: "error"; kind: SettlementError["kind"] };

const kindOf = (error: unknown): SettlementError["kind"] => (error instanceof SettlementError ? error.kind : "unavailable");

function readCopy(kind: SettlementError["kind"]): string {
  if (kind === "access") return "La cuenta no tiene habilitación activa para ver liquidaciones.";
  if (kind === "validation") return "La API no aceptó la fecha.";
  return "No se pudo consultar la liquidación. Reintentar en unos minutos.";
}

function writeCopy(kind: SettlementError["kind"]): string {
  if (kind === "draft_changed") return "El borrador cambió desde que lo viste: volvé a consultarlo antes de dar conformidad.";
  if (kind === "period_state") return "El período ya no está en revisión: volvé a consultarlo.";
  if (kind === "validation") return "La API no aceptó el pedido: revisá el comentario (sin mails, enlaces ni números largos).";
  if (kind === "access") return "La cuenta no tiene habilitación activa en esta planta.";
  return "No se pudo registrar. Reintentar en unos minutos.";
}

const STATUS_VARIANT = { open: "neutro", in_review: "ambar", closed: "ok" } as const;

export function Settlement({ context, port, save = saveWithAnchor }: Props): React.JSX.Element {
  const baseId = useId();
  const { session, siteId } = context;
  const [summary, setSummary] = useState<SummaryState>({ estado: "cargando" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;
    setSummary({ estado: "cargando" });
    port.summary(session).then(
      (result) => {
        if (current) setSummary({ estado: "listo", items: result.items });
      },
      (error: unknown) => {
        if (current) setSummary({ estado: "error", kind: kindOf(error) });
      },
    );
    return () => {
      current = false;
    };
  }, [port, session, attempt]);

  const header = (
    <div>
      <p className="m-0 text-eyebrow font-semibold uppercase tracking-eyebrow text-acero-texto">
        {siteId === null ? "Todas las plantas" : (context.sites.find((s) => s.siteId === siteId)?.name ?? siteId)}
      </p>
      <h1 id={`${baseId}-title`} className="m-0 text-xl font-semibold leading-titulo tracking-titulo text-blanco">
        Liquidación
      </h1>
      <p className="m-0 text-xs text-texto-suave">
        Lo que la empresa le cobra a cada planta por los viajes del período, calculado por UCORP con las tarifas acordadas. Borrador: no es comprobante fiscal.
      </p>
    </div>
  );

  if (summary.estado === "cargando") {
    return (
      <section aria-labelledby={`${baseId}-title`} className="flex flex-col gap-4">
        {header}
        <SkeletonTable rows={3} cols={4} label="Consultando las liquidaciones" />
      </section>
    );
  }
  if (summary.estado === "error") {
    return (
      <section aria-labelledby={`${baseId}-title`} className="flex flex-col gap-4">
        {header}
        <Failure title={readCopy(summary.kind)} onRetry={() => setAttempt((n) => n + 1)} />
      </section>
    );
  }

  const items = summaryForSite(summary.items, siteId);
  return (
    <section aria-labelledby={`${baseId}-title`} className="flex flex-col gap-4">
      {header}
      {siteId === null ? (
        <SummaryTable id={`${baseId}-summary`} items={items} onSelect={context.selectSite} />
      ) : (
        <SiteSettlement key={siteId} id={`${baseId}-site`} session={session} port={port} siteId={siteId} items={items} save={save} />
      )}
    </section>
  );
}

function SummaryTable({ id, items, onSelect }: { id: string; items: readonly ProviderSettlementSummary[]; onSelect: (siteId: string) => void }): React.JSX.Element {
  const [all, setAll] = useState(false);
  const { shown, rest } = limited(items, SETTLEMENT_SITES_SHOWN, all);
  if (items.length === 0) {
    return <EmptyState title="Sin plantas habilitadas">La cuenta no tiene habilitaciones activas: no hay liquidaciones que mostrar.</EmptyState>;
  }
  return (
    <Card role="region" aria-labelledby={id} className="gap-0 p-0">
      <PanelHeader id={id} title="Por planta" />
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-divisor">
              <th scope="col" className="rotulo h-9 px-3.5 text-texto-suave">Planta</th>
              <th scope="col" className="rotulo h-9 px-3.5 text-texto-suave">Período</th>
              <th scope="col" className="rotulo h-9 px-3.5 text-texto-suave">Viajes</th>
              <th scope="col" className="rotulo h-9 px-3.5 text-right text-texto-suave">Total del período</th>
              <th scope="col" className="h-9 px-3.5" />
            </tr>
          </thead>
          <tbody>
            {shown.map((item) => (
              <tr key={`${item.siteId}:${item.providerId}`} className="h-fila border-b border-divisor text-texto-2 last:border-b-0 hover:bg-velo" data-site={item.siteId}>
                <td className="px-3.5 py-1 text-texto">{item.siteName}</td>
                <td className="tabular whitespace-nowrap px-3.5 py-1">
                  {item.mode === "full" ? `${formatCalendarDate(item.period.from)} al ${formatCalendarDate(item.period.to)}` : ""}
                </td>
                <td className="tabular px-3.5 py-1">{item.mode === "full" ? `${String(item.chargedTripCount)} de ${String(item.tripCount)}` : ""}</td>
                <td className="tabular whitespace-nowrap px-3.5 py-1 text-right text-texto">{summaryTotalText(item)}</td>
                <td className="px-3.5 py-1 text-right">
                  <Button variant="secundario" size="sm" onClick={() => onSelect(item.siteId)}>
                    Ver
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rest > 0 && (
        <div className="border-t border-divisor px-3.5 py-2">
          <Button variant="fantasma" size="sm" onClick={() => setAll(true)}>
            {`Ver ${rest === 1 ? "la otra planta" : `las otras ${String(rest)} plantas`}`}
          </Button>
        </div>
      )}
    </Card>
  );
}

interface SiteProps {
  id: string;
  session: PortalSession;
  port: SettlementPort;
  siteId: string;
  items: readonly ProviderSettlementSummary[];
  save: SaveFile;
}

function SiteSettlement({ id, session, port, siteId, items, save }: SiteProps): React.JSX.Element {
  const summary = items[0];
  const [date, setDate] = useState(summary?.date ?? "");
  const [view, setView] = useState<DraftState>({ estado: "inicial" });
  const [downloading, setDownloading] = useState<"csv" | "xlsx" | null>(null);
  const [downloadError, setDownloadError] = useState<string | undefined>(undefined);
  const lastRead = useRef(0);
  const full = summary?.mode === "full";

  function read(asked: string): void {
    if (!isCalendarDate(asked)) return;
    lastRead.current += 1;
    const ticket = lastRead.current;
    setView({ estado: "cargando" });
    port.draft(session, siteId, asked).then(
      (draft) => {
        if (ticket === lastRead.current) setView({ estado: "listo", date: asked, draft });
      },
      (error: unknown) => {
        if (ticket === lastRead.current) setView({ estado: "error", kind: kindOf(error) });
      },
    );
  }

  // El periodo de hoy se lee solo al entrar; otra fecha se pide con "Ver".
  useEffect(() => {
    if (full && summary !== undefined) read(summary.date);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar la planta (la key la remonta)
  }, []);

  async function download(format: "csv" | "xlsx"): Promise<void> {
    if (!isCalendarDate(date)) return;
    setDownloading(format);
    setDownloadError(undefined);
    try {
      const file = await port.download(session, siteId, date, format);
      save(file.blob, file.fileName);
    } catch (error) {
      setDownloadError(kindOf(error) === "access" ? readCopy("access") : "No se pudo descargar el archivo. Reintentar en unos minutos.");
    } finally {
      setDownloading(null);
    }
  }

  if (summary === undefined) {
    return <EmptyState title="Sin habilitación en esta planta">La empresa no tiene habilitación activa acá.</EmptyState>;
  }
  if (!full) {
    return (
      <EmptyState icon="exportable" title="Exportable sin montos" actions={<a className="text-sm text-acero-texto underline" href={hrefFor({ path: "/trip-export", siteId })}>Ir al Exportable</a>}>
        {EXPORT_ONLY_COPY}
      </EmptyState>
    );
  }

  const valid = isCalendarDate(date);
  return (
    <>
      <Card className="gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-date`}>Fecha del período</Label>
            <Input id={`${id}-date`} type="date" className="w-auto" value={date} aria-invalid={date !== "" && !valid} onChange={(event) => setDate(event.target.value)} />
          </div>
          <Button disabled={!valid || view.estado === "cargando"} onClick={() => read(date)}>
            Ver
          </Button>
          <Button variant="secundario" disabled={!valid || downloading !== null} onClick={() => void download("csv")}>
            <Icon name="exportable" />
            {downloading === "csv" ? "Descargando..." : "Descargar CSV"}
          </Button>
          <Button variant="secundario" disabled={!valid || downloading !== null} onClick={() => void download("xlsx")}>
            <Icon name="exportable" />
            {downloading === "xlsx" ? "Descargando..." : "Descargar XLSX"}
          </Button>
        </div>
        <p className="m-0 text-xs text-texto-suave">
          Cualquier día del período sirve: el período y las tarifas los carga UCORP con lo acordado con la planta. Cada lectura y descarga queda registrada.
        </p>
        {downloadError !== undefined && <Failure title={downloadError} />}
      </Card>
      {view.estado === "cargando" && <SkeletonTable rows={4} cols={5} label="Calculando la liquidación" />}
      {view.estado === "error" && <Failure title={view.kind === "not_full" ? EXPORT_ONLY_COPY : readCopy(view.kind)} onRetry={() => read(date)} />}
      {view.estado === "listo" && (
        <>
          <ReviewPanel id={`${id}-review`} session={session} port={port} siteId={siteId} draft={view.draft} onChanged={() => read(view.date)} />
          <DraftDetail id={`${id}-draft`} draft={view.draft} timezone={summary.timezone} session={session} port={port} siteId={siteId} onChanged={() => read(view.date)} />
        </>
      )}
    </>
  );
}

interface ReviewProps {
  id: string;
  session: PortalSession;
  port: SettlementPort;
  siteId: string;
  draft: SettlementDraft;
  onChanged: () => void;
}

/** Estado, plazo, conformidad de las dos partes y observaciones del periodo (LQ-6). */
function ReviewPanel({ id, session, port, siteId, draft, onChanged }: ReviewProps): React.JSX.Element {
  const review: SettlementReview = draft.review;
  const [state, setState] = useState<{ fase: "quieta" } | { fase: "enviando" } | { fase: "error"; kind: SettlementError["kind"] } | { fase: "listo" }>({ fase: "quieta" });
  const periodId = draft.lastRun?.id;
  const own = approvalState(review, "provider");
  const canApprove = review.status === "in_review" && own !== "current" && periodId !== undefined;

  async function approve(): Promise<void> {
    if (periodId === undefined) return;
    setState({ fase: "enviando" });
    try {
      await port.approve(session, siteId, periodId, draft.inputsHash);
      setState({ fase: "listo" });
      onChanged();
    } catch (error) {
      setState({ fase: "error", kind: kindOf(error) });
    }
  }

  return (
    <Card role="region" aria-labelledby={id} className="gap-0 p-0" data-status={review.status}>
      <PanelHeader id={id} title="Revisión del período" />
      <div className="flex flex-wrap items-center gap-3 border-b border-divisor px-3.5 py-3 text-sm">
        <Badge variant={STATUS_VARIANT[review.status]}>{PERIOD_STATUS_LABEL[review.status]}</Badge>
        {review.status === "open" && <span className="text-texto-suave">UCORP la pasa a revisión cuando termina el período.</span>}
        {review.status === "in_review" && review.reviewUntil !== undefined && <span className="text-texto-suave">Se puede observar y dar conformidad hasta el {formatCalendarDate(review.reviewUntil)} inclusive.</span>}
        {review.status === "closed" && <span className="text-texto-suave">Cerrado por UCORP: lo que se ve es lo congelado al cierre y no cambia. Una diferencia posterior sale como nota de ajuste en el período siguiente.</span>}
      </div>
      <dl className="tabular m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-b border-divisor px-3.5 py-3 text-sm">
        {(["provider", "site"] as const).map((party) => (
          <Fragment key={party}>
            <dt className="text-texto-suave">{PARTY_LABEL[party]}</dt>
            <dd className="m-0 text-texto" data-approval={party}>
              {APPROVAL_TEXT[approvalState(review, party)]}
            </dd>
          </Fragment>
        ))}
      </dl>
      {review.status === "in_review" && (
        <div className="flex flex-wrap items-center gap-3 border-b border-divisor px-3.5 py-3">
          <Button disabled={!canApprove || state.fase === "enviando"} onClick={() => void approve()}>
            {state.fase === "enviando" ? "Registrando..." : "Dar conformidad"}
          </Button>
          <span className="text-xs text-texto-suave">
            {own === "current" ? "La empresa ya dio conformidad a este borrador." : own === "stale" ? "El borrador cambió después de la conformidad anterior: si estás de acuerdo con el vigente, volvé a darla." : "La conformidad es sobre el borrador tal como se ve ahora; si UCORP lo recalcula, se pide de nuevo."}
          </span>
          {state.fase === "error" && <Failure title={writeCopy(state.kind)} />}
        </div>
      )}
      <div className="px-3.5 py-3">
        <p className="m-0 mb-2 text-eyebrow font-semibold uppercase tracking-eyebrow text-texto-suave">Observaciones</p>
        {review.disputes.length === 0 ? (
          <p role="status" className="m-0 text-sm text-texto-suave">
            Sin observaciones. {review.status === "in_review" ? "Cada viaje tiene el botón Observar." : ""}
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0 text-sm">
            {review.disputes.map((dispute) => (
              <li key={dispute.id} className="flex flex-col gap-0.5" data-dispute={dispute.id}>
                <span className="text-texto">
                  {PARTY_LABEL[dispute.party]} observó <span className="tabular text-xs">{dispute.tripId}</span>: {DISPUTE_REASON_LABEL[dispute.reason]}
                  {dispute.status === "open" ? <Badge variant="ambar" className="ml-2">Abierta</Badge> : <Badge variant="ok" className="ml-2">Resuelta</Badge>}
                </span>
                {dispute.comment !== undefined && <span className="text-xs text-texto-2">{dispute.comment}</span>}
                {dispute.resolution !== undefined && (
                  <span className="text-xs text-texto-suave">
                    {DISPUTE_RESOLUTION_LABEL[dispute.resolution]}
                    {dispute.resolutionComment !== undefined ? `: ${dispute.resolutionComment}` : ""}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

interface DisputeFormProps {
  session: PortalSession;
  port: SettlementPort;
  siteId: string;
  periodId: string;
  trip: TripSettlement;
  onDone: () => void;
  onCancel: () => void;
}

/** Observar un viaje o una de sus lineas: motivo del catalogo y comentario acotado sin datos de personas. */
function DisputeForm({ session, port, siteId, periodId, trip, onDone, onCancel }: DisputeFormProps): React.JSX.Element {
  const baseId = useId();
  const [reason, setReason] = useState<SettlementDisputeReason>("distance");
  const [rateLineId, setRateLineId] = useState("");
  const [comment, setComment] = useState("");
  const [state, setState] = useState<{ fase: "quieta" } | { fase: "enviando" } | { fase: "error"; kind: SettlementError["kind"] }>({ fase: "quieta" });
  const problem = comment.trim() === "" ? null : disputeCommentProblem(comment.trim());
  const request = disputeRequest({ tripId: trip.tripId, rateLineId, reason, comment });

  async function send(): Promise<void> {
    if (request === null) return;
    setState({ fase: "enviando" });
    try {
      await port.dispute(session, siteId, periodId, request);
      onDone();
    } catch (error) {
      setState({ fase: "error", kind: kindOf(error) });
    }
  }

  return (
    <form
      role="group"
      aria-label={`Observar el viaje ${trip.tripId}`}
      className="flex flex-col gap-3 rounded-md border border-linea bg-velo p-3"
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${baseId}-reason`}>Motivo</Label>
          <select id={`${baseId}-reason`} className="desplegable h-10 w-full rounded-md border border-linea-fuerte bg-hundida pl-3 pr-8 text-base text-texto" value={reason} onChange={(event) => setReason(event.target.value as SettlementDisputeReason)}>
            {DISPUTE_REASONS.map((r) => (
              <option key={r} value={r}>
                {DISPUTE_REASON_LABEL[r]}
              </option>
            ))}
          </select>
        </div>
        {trip.lines.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${baseId}-line`}>Línea</Label>
            <select id={`${baseId}-line`} className="desplegable h-10 w-full rounded-md border border-linea-fuerte bg-hundida pl-3 pr-8 text-base text-texto" value={rateLineId} onChange={(event) => setRateLineId(event.target.value)}>
              <option value="">Todo el viaje</option>
              {trip.lines.map((line) => (
                <option key={line.rateLineId} value={line.rateLineId}>
                  {CONCEPT_LABEL[line.concept]}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${baseId}-comment`}>Comentario (opcional)</Label>
        <textarea
          id={`${baseId}-comment`}
          rows={2}
          maxLength={DISPUTE_COMMENT_MAX + 50}
          value={comment}
          aria-invalid={problem !== null}
          onChange={(event) => setComment(event.target.value)}
          className="w-full rounded-md border border-linea-fuerte bg-hundida px-3 py-2 font-sans text-sm text-texto aria-invalid:border-critico"
        />
        <span className="text-xs text-ambar">Sin datos de personas: ni nombres, ni mails, ni teléfonos, ni legajos. La planta y UCORP lo leen.</span>
        {problem !== null && <span className="text-xs text-critico-texto">{DISPUTE_COMMENT_PROBLEM_TEXT[problem]}</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={request === null || state.fase === "enviando"}>
          {state.fase === "enviando" ? "Enviando..." : "Enviar observación"}
        </Button>
        <Button type="button" size="sm" variant="secundario" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
      {state.fase === "error" && <Failure title={writeCopy(state.kind)} />}
    </form>
  );
}

interface DetailProps {
  id: string;
  draft: SettlementDraft;
  /** Zona IANA de la planta: las horas de la espera van en esta zona (regla 9). */
  timezone: string;
  session: PortalSession;
  port: SettlementPort;
  siteId: string;
  onChanged: () => void;
}

function DraftDetail({ id, draft, timezone, session, port, siteId, onChanged }: DetailProps): React.JSX.Element {
  const [all, setAll] = useState(false);
  const waits = waitsByTrip(draft);
  const [disputing, setDisputing] = useState<string | null>(null);
  const { shown, rest } = limited(draft.trips, SETTLEMENT_TRIPS_SHOWN, all);
  const currency = draft.currency;
  const periodId = draft.review.status === "in_review" ? draft.lastRun?.id : undefined;
  return (
    <Card role="region" aria-labelledby={id} className="gap-0 p-0">
      <PanelHeader id={id} title={`Del ${formatCalendarDate(draft.period.from)} al ${formatCalendarDate(draft.period.to)}`} />
      <dl className="tabular m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-b border-divisor px-3.5 py-3 text-sm">
        <dt className="text-texto-suave">Total del período</dt>
        <dd className="m-0 text-lg font-semibold text-blanco" data-testid="settlement-total">{formatMinor(draft.totals.totalMinor, currency)}</dd>
        <dt className="text-texto-suave">Viajes</dt>
        <dd className="m-0 text-texto">{tripsCountText(draft)}</dd>
        {draft.adjustments.length > 0 && (
          <>
            <dt className="text-texto-suave">Notas de ajuste</dt>
            <dd className="m-0 text-texto" data-testid="settlement-adjustments">
              {formatMinor(draft.totals.adjustmentsMinor, currency)} en {draft.adjustments.length} {draft.adjustments.length === 1 ? "nota" : "notas"} (viajes del período: {formatMinor(draft.totals.tripsMinor, currency)})
            </dd>
          </>
        )}
        {draft.stale && (
          <>
            <dt className="text-texto-suave">Estado</dt>
            <dd className="m-0 text-texto">
              <Badge variant="ambar">Cambió desde la última corrida de UCORP</Badge>
            </dd>
          </>
        )}
      </dl>
      {draft.totals.byConcept.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-x-5 gap-y-1 border-b border-divisor px-3.5 py-2 text-xs text-texto-2">
          {draft.totals.byConcept.map((row) => (
            <li key={row.concept} className="tabular">
              <span className="text-texto-suave">{CONCEPT_LABEL[row.concept]} </span>
              {formatMinor(row.amountMinor, currency)}
            </li>
          ))}
        </ul>
      )}
      {draft.trips.length === 0 ? (
        <p role="status" className="m-0 px-3.5 py-3 text-sm text-texto-suave">
          No hay viajes sellados en este período.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-divisor">
                  <th scope="col" className="rotulo h-9 whitespace-nowrap px-3.5 text-texto-suave">Fecha</th>
                  <th scope="col" className="rotulo h-9 px-3.5 text-texto-suave">Viaje</th>
                  <th scope="col" className="rotulo h-9 px-3.5 text-texto-suave">Servicio</th>
                  <th scope="col" className="rotulo h-9 px-3.5 text-texto-suave">Resultado</th>
                  <th scope="col" className="rotulo h-9 whitespace-nowrap px-3.5 text-texto-suave">Espera</th>
                  <th scope="col" className="rotulo h-9 whitespace-nowrap px-3.5 text-right text-texto-suave">Total</th>
                  <th scope="col" className="h-9 px-3.5" />
                </tr>
              </thead>
              <tbody>
                {shown.map((trip) => (
                  <TripRow
                    key={trip.tripId}
                    trip={trip}
                    wait={waits.get(trip.tripId)}
                    timezone={timezone}
                    currency={currency}
                    canDispute={periodId !== undefined}
                    onDispute={() => setDisputing(trip.tripId)}
                    form={
                      periodId !== undefined && disputing === trip.tripId ? (
                        <DisputeForm
                          session={session}
                          port={port}
                          siteId={siteId}
                          periodId={periodId}
                          trip={trip}
                          onDone={() => {
                            setDisputing(null);
                            onChanged();
                          }}
                          onCancel={() => setDisputing(null)}
                        />
                      ) : null
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
          {rest > 0 && (
            <div className="border-t border-divisor px-3.5 py-2">
              <Button variant="fantasma" size="sm" onClick={() => setAll(true)}>
                {`Ver ${rest === 1 ? "el otro viaje" : `los otros ${String(rest)} viajes`}`}
              </Button>
            </div>
          )}
        </>
      )}
      {draft.adjustments.length > 0 && (
        <div className="border-t border-divisor px-3.5 py-3" data-testid="adjustments">
          <p className="m-0 mb-2 text-eyebrow font-semibold uppercase tracking-eyebrow text-texto-suave">Notas de ajuste de períodos cerrados</p>
          <ul className="tabular m-0 flex list-none flex-col gap-1 p-0 text-sm text-texto-2">
            {draft.adjustments.map((adjustment) => (
              <li key={adjustment.id} data-adjustment={adjustment.id}>
                <span className="text-xs">{adjustment.tripId}</span> del período {formatCalendarDate(adjustment.originFrom)} al {formatCalendarDate(adjustment.originTo)} ({ADJUSTMENT_REASON_LABEL[adjustment.reason]}):{" "}
                {formatMinor(adjustment.previousTotalMinor, currency)} cerrado, {formatMinor(adjustment.currentTotalMinor, currency)} recalculado, ajuste <span className="text-texto">{formatMinor(adjustment.amountMinor, currency)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/**
 * Espera del viaje para auditarla (LQ-8): fuente, la cuenta con la tolerancia
 * de cobro del tarifario y, por parada de subida, llegada, desde y hasta
 * cuando cuenta, y cada Subió o No se presentó. Sin personas ni direcciones.
 */
function WaitDetail({ wait, timezone }: { wait: TripWait; timezone: string }): React.JSX.Element {
  const rows = waitStopRows(wait, timezone);
  const fallback = waitFallbackText(wait);
  return (
    <div className="mt-2 border-t border-divisor pt-2" data-trip-wait={wait.tripId}>
      <p className="m-0 text-xs text-texto-2">
        <span className="text-texto-suave">Espera: </span>
        {wait.waitSource === undefined ? "sin dato" : WAIT_SOURCE_LABEL[wait.waitSource]}
        {wait.corrected && " (corregida por UCORP)"}. {waitBillingText(wait)}
      </p>
      {fallback !== null && <p className="m-0 mt-1 text-xs text-texto-suave">{fallback}</p>}
      {rows.length > 0 && (
        <table className="mt-2 w-full border-collapse text-left text-xs">
          <caption className="sr-only">Espera por parada del viaje {wait.tripId}</caption>
          <thead>
            <tr className="border-b border-divisor text-texto-suave">
              <th scope="col" className="py-1 pr-3 font-medium">Parada</th>
              <th scope="col" className="py-1 pr-3 font-medium">Planificada</th>
              <th scope="col" className="py-1 pr-3 font-medium">Llegada</th>
              <th scope="col" className="py-1 pr-3 font-medium">Cuenta desde</th>
              <th scope="col" className="py-1 pr-3 font-medium">Hasta</th>
              <th scope="col" className="py-1 pr-3 font-medium">Espera</th>
              <th scope="col" className="py-1 font-medium">Subida o no-show</th>
            </tr>
          </thead>
          <tbody className="tabular text-texto-2">
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-divisor last:border-0" data-wait-stop={row.key}>
                <td className="py-1 pr-3">{row.stop}</td>
                <td className="py-1 pr-3">{row.planned}</td>
                <td className="py-1 pr-3">{row.arrived}</td>
                <td className="py-1 pr-3">{row.from}</td>
                <td className="py-1 pr-3">{row.until}</td>
                <td className="py-1 pr-3 text-texto">{row.waited}</td>
                <td className="py-1">{row.events}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface RowProps {
  trip: TripSettlement;
  wait: TripWait | undefined;
  timezone: string;
  currency: string;
  canDispute: boolean;
  onDispute: () => void;
  form: React.ReactNode;
}

function TripRow({ trip, wait, timezone, currency, canDispute, onDispute, form }: RowProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="h-fila border-b border-divisor text-texto-2 hover:bg-velo" data-trip={trip.tripId}>
        <td className="tabular whitespace-nowrap px-3.5 py-1">{formatCalendarDate(trip.serviceDate)}</td>
        <td className="tabular px-3.5 py-1 text-xs">{trip.tripId}</td>
        <td className="px-3.5 py-1">{serviceLabel(trip)}</td>
        <td className="px-3.5 py-1">
          {OUTCOME_LABEL[trip.outcome]}
          {trip.correctedFields.length > 0 && (
            <Badge variant="acero" className="ml-2">
              Corregido
            </Badge>
          )}
        </td>
        <td className="tabular whitespace-nowrap px-3.5 py-1" data-wait={wait?.waitSource ?? "none"}>
          {wait === undefined ? (
            <span className="text-texto-suave">-</span>
          ) : (
            <>
              <span className="text-texto">{waitMinutesText(wait)}</span>
              {wait.waitSource !== undefined && (
                <Badge variant={wait.waitSource === "driver_app" ? "ok" : "neutro"} className="ml-2">
                  {wait.waitSource === "driver_app" ? "Conductor" : "GPS"}
                </Badge>
              )}
              {waitBillableText(wait) !== null && <div className="text-xs text-texto-suave">{waitBillableText(wait)}</div>}
            </>
          )}
        </td>
        <td className="tabular whitespace-nowrap px-3.5 py-1 text-right text-texto">{tripTotalText(trip, currency)}</td>
        <td className="whitespace-nowrap px-3.5 py-1 text-right">
          {canDispute && (
            <Button variant="fantasma" size="sm" aria-label={`Observar el viaje ${trip.tripId}`} onClick={onDispute}>
              Observar
            </Button>
          )}
          {(trip.lines.length > 0 || wait !== undefined) && (
            <Button variant="fantasma" size="sm" aria-expanded={open} aria-label={`${open ? "Ocultar" : "Ver"} detalle del viaje ${trip.tripId}`} onClick={() => setOpen((v) => !v)}>
              {open ? "Ocultar" : "Detalle"}
            </Button>
          )}
        </td>
      </tr>
      {form !== null && (
        <tr className="border-b border-divisor" data-trip-dispute={trip.tripId}>
          <td colSpan={7} className="px-3.5 py-2">
            {form}
          </td>
        </tr>
      )}
      {open && (
        <tr className="border-b border-divisor" data-trip-lines={trip.tripId}>
          <td colSpan={7} className="px-3.5 py-2">
            <ul className="m-0 flex list-none flex-col gap-1 p-0 text-xs text-texto-2">
              {trip.lines.map((line) => (
                <li key={line.rateLineId} className="tabular">
                  <span className="text-texto-suave">{CONCEPT_LABEL[line.concept]}: </span>
                  {lineText(line)}
                </li>
              ))}
            </ul>
            {wait !== undefined && <WaitDetail wait={wait} timezone={timezone} />}
          </td>
        </tr>
      )}
    </>
  );
}
